import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Flex } from 'components/common/styled/common-styled';
import { currencyFormat } from 'utils';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { useCalculateAdvertisedTierPrice } from 'utils/calculateAdvertisedTierPrice';
import { ResourceParagraph } from 'components/theme/Typography/ResourceTypography';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import { SkeletonPulse } from 'components/theme/Skeleton/Skeleton';
import type { Brand } from 'api/brand/fetchBrandById';
import { BrandFlagContainer } from 'components/molecules/BrandFlagContainer';
import { MediaV3 } from '@kouto/types';
import type { ITax } from 'types/experience.types';

interface ResourceGroupHeroProps {
  displayedCapacity?: number | null;
  price?: number;
  taxes?: ITax[];
  title: string;
  heroImage: MediaV3[];
  promoterBrand?: Brand;
  brandFlagName?: string;
}

const GroupHero = ({
  displayedCapacity,
  heroImage,
  title,
  price,
  taxes = [],
  promoterBrand,
  brandFlagName,
}: ResourceGroupHeroProps) => {
  const currency = useBrandCurrency();
  const { t: translate } = useTranslation();
  const { isLoading } = useResourceBookingSession();
  const calculateAdvertisedTierPrice = useCalculateAdvertisedTierPrice();

  let detailsLabel = '';
  if (currency && price && Number.isFinite(price) && !Number.isNaN(price)) {
    const displayPrice = calculateAdvertisedTierPrice(price, taxes);
    detailsLabel = `${translate('from')} ${currencyFormat(currency)(
      displayPrice,
    )}`;
  }
  if (currency && displayedCapacity) {
    detailsLabel += ' • ';
  }
  if (displayedCapacity) {
    detailsLabel += `${displayedCapacity} ${translate(
      displayedCapacity === 1 ? 'person' : 'people',
    )}`;
  }

  return (
    <Flex direction="column" alignItem="flex-start" gap={24}>
      <BrandFlagContainer brandFlagName={brandFlagName}>
        <HeroImageWrapper>
          <ResourceCoverImage
            src={heroImage?.[0]?.links?.[0]?.url}
            alt={heroImage?.[0]?.altText || title}
            isSixteenNineRatio
          />
        </HeroImageWrapper>
      </BrandFlagContainer>
      <Flex
        direction="column"
        gap={promoterBrand?.name ? 8 : 12}
        alignItem="flex-start"
      >
        <GroupTitle
          as="h1"
          data-testid="resource-group-title"
          className="resource-group-title"
          promoterBrand={!!promoterBrand?.name}
        >
          {title}
        </GroupTitle>
        {promoterBrand?.name && (
          <BrandName>
            {translate('by')} {promoterBrand.name}
          </BrandName>
        )}
        {detailsLabel && isLoading ? (
          <SkeletonPulse style={{ height: 18 }} />
        ) : (
          <CurrencyWrapper>
            <ResourcePagePriceText
              lineHeight="24px"
              fontSize="16px"
              className="resource-page__resource-starting-price"
            >
              {detailsLabel}
            </ResourcePagePriceText>
          </CurrencyWrapper>
        )}
      </Flex>
    </Flex>
  );
};

const HeroImageWrapper = styled.div`
  margin-bottom: 0;
  display: flex;
  position: relative;
  width: 100%;
  overflow: hidden;
  height: 279px;
  max-width: 481px;
  align-items: center;
  justify-content: center;
`;

const CurrencyWrapper = styled.span`
  margin: 0px;
`;

const GroupTitle = styled.span<{ promoterBrand?: boolean }>`
  font-size: 28px;
  line-height: 40px;
  font-weight: 700;
  white-space: normal;
  overflow: hidden;
  margin-bottom: ${({ promoterBrand }) => (promoterBrand ? '0px' : '')};
`;

export const BrandName = styled.p`
  color: var(--Text-Tertiary, rgba(0, 0, 0, 0.6));
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.016px;
  margin-top: 0px;
  margin-bottom: 8px;
`;

const ResourceCoverImage = styled.img<{ isSixteenNineRatio: boolean }>`
  height: 279px;
  object-fit: cover;
  border-radius: var(--way-design-borderRadiusDefault);
  object-position: center;
`;

const ResourcePagePriceText = styled(ResourceParagraph)`
  && {
    margin-right: 10px;
    display: inline;
  }
`;

export default GroupHero;
